import React from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { FormattedMessage, injectIntl } from 'react-intl';
import cx from 'classnames';

import s from './FeaturedCars.css';
import btn from '../shared/landingButtons.css';
import messages from '../../../locale/messages';
import {
  FEATURED_CAR_BASE_URL,
  FEATURED_CARS_PAGE_SIZE,
  LUXURY_PRICE_MAX_USD,
  createDefaultPriceRanges,
  filterFeaturedCarsByMinPrice,
  filterFeaturedCarsByPrice,
  getFeaturedCarsPage,
  getFeaturedCarsTotalPages,
  getFeaturedPriceRange,
  getFilteredFeaturedCars,
} from './featuredCarsData';
import FeaturedCarCard, { FeaturedCarCardPlaceholder } from './FeaturedCarCard';
import FeaturedCarsFilters from './FeaturedCarsFilters';

class FeaturedCars extends React.Component {
  static propTypes = {
    intl: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      activeFilter: 'all',
      pageIndex: 0,
      openPricePopover: null,
      priceRangeDraft: createDefaultPriceRanges(),
      appliedPriceRanges: createDefaultPriceRanges(),
      filteredCars: getFilteredFeaturedCars('all'),
    };

    this.wrapperRefs = {};
    this.btnWrapperRefs = {};
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  setBtnWrapperRef = (filterKey, node) => {
    this.btnWrapperRefs[filterKey] = node;
  };

  setPopoverWrapperRef = (filterKey, node) => {
    this.wrapperRefs[filterKey] = node;
  };

  handleClickOutside = (event) => {
    const { openPricePopover } = this.state;
    if (!openPricePopover) {
      return;
    }

    const wrapper = this.wrapperRefs[openPricePopover];
    const btnWrapper = this.btnWrapperRefs[openPricePopover];

    if (wrapper && !wrapper.contains(event.target)
      && btnWrapper && !btnWrapper.contains(event.target)) {
      this.setState({ openPricePopover: null });
    }
  };

  handleFilterClick = (filterKey) => {
    this.setState({
      activeFilter: filterKey,
      pageIndex: 0,
      openPricePopover: null,
      filteredCars: getFilteredFeaturedCars(filterKey),
    });
  };

  isFilterActive = (filterKey) => {
    const { activeFilter, openPricePopover } = this.state;

    if (openPricePopover) {
      return openPricePopover === filterKey;
    }

    return activeFilter === filterKey;
  };

  handlePriceFilterToggle = (filterKey) => {
    this.setState((prev) => {
      const isClosing = prev.openPricePopover === filterKey;

      if (isClosing) {
        return { openPricePopover: null };
      }

      const draftRange = prev.activeFilter === filterKey
        ? prev.appliedPriceRanges[filterKey]
        : getFeaturedPriceRange(filterKey);

      return {
        openPricePopover: filterKey,
        priceRangeDraft: {
          ...prev.priceRangeDraft,
          [filterKey]: draftRange,
        },
      };
    });
  };

  handlePriceRangeChange = (filterKey, range) => {
    this.setState((prev) => ({
      priceRangeDraft: {
        ...prev.priceRangeDraft,
        [filterKey]: range,
      },
    }));
  };

  handlePriceFilterApply = (filterKey) => {
    const [minPrice, maxPrice] = this.state.priceRangeDraft[filterKey];
    const filteredCars = filterKey === 'luxury'
      ? filterFeaturedCarsByMinPrice(minPrice)
      : filterFeaturedCarsByPrice(minPrice, maxPrice);

    this.setState((prev) => ({
      activeFilter: filterKey,
      pageIndex: 0,
      openPricePopover: null,
      appliedPriceRanges: {
        ...prev.appliedPriceRanges,
        [filterKey]: filterKey === 'luxury'
          ? [minPrice, LUXURY_PRICE_MAX_USD]
          : [minPrice, maxPrice],
      },
      filteredCars,
    }));
  };

  handlePrev = () => {
    this.setState((prev) => ({
      pageIndex: Math.max(0, prev.pageIndex - 1),
    }));
  };

  handleNext = () => {
    const { filteredCars } = this.state;
    const totalPages = getFeaturedCarsTotalPages(filteredCars);

    this.setState((prev) => ({
      pageIndex: Math.min(totalPages - 1, prev.pageIndex + 1),
    }));
  };

  render() {
    const { intl } = this.props;
    const {
      activeFilter,
      pageIndex,
      openPricePopover,
      priceRangeDraft,
      appliedPriceRanges,
      filteredCars,
    } = this.state;
    const totalPages = getFeaturedCarsTotalPages(filteredCars);
    const safePageIndex = Math.min(pageIndex, totalPages - 1);
    const visibleCars = getFeaturedCarsPage(filteredCars, safePageIndex);
    const gridSlots = Array.from(
      { length: FEATURED_CARS_PAGE_SIZE },
      (_, index) => visibleCars[index] || null,
    );
    const canGoPrev = filteredCars.length > 0 && safePageIndex > 0;
    const canGoNext = filteredCars.length > FEATURED_CARS_PAGE_SIZE
      && safePageIndex < totalPages - 1;

    return (
      <section id="explore" className={s.exploreSection}>
        <FeaturedCarsFilters
          activeFilter={activeFilter}
          openPricePopover={openPricePopover}
          priceRangeDraft={priceRangeDraft}
          appliedPriceRanges={appliedPriceRanges}
          isFilterActive={this.isFilterActive}
          onFilterClick={this.handleFilterClick}
          onPriceFilterToggle={this.handlePriceFilterToggle}
          onPriceRangeChange={this.handlePriceRangeChange}
          onPriceFilterApply={this.handlePriceFilterApply}
          setBtnWrapperRef={this.setBtnWrapperRef}
          setPopoverWrapperRef={this.setPopoverWrapperRef}
        />

        <div className={cx(s.exploreHeaderRow, 'directionLtr')}>
          <div>
            <h2 className={s.exploreTitle}><FormattedMessage {...messages.newLandingCarRentalsInLyon} /></h2>
            <p className={s.exploreSubtitle}><FormattedMessage {...messages.newLandingAverageDailyPrices} /></p>
          </div>
          <div className={cx(s.exploreNavArrows, 'directionLtr')}>
            <button
              type="button"
              className={cx(s.navArrow, !canGoPrev && s.navArrowDisabled)}
              onClick={this.handlePrev}
              disabled={!canGoPrev}
              aria-label={intl.formatMessage(messages.newLandingPrevious)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <button
              type="button"
              className={cx(s.navArrow, !canGoNext && s.navArrowDisabled)}
              onClick={this.handleNext}
              disabled={!canGoNext}
              aria-label={intl.formatMessage(messages.newLandingNext)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          </div>
        </div>

        <div className={s.carGridWrapper}>
          {visibleCars.length === 0 ? (
            <p className={s.emptyState}>
              <FormattedMessage {...messages.newLandingNoCarsInFilter} />
            </p>
          ) : (
            <div className={s.carGridModern}>
              {gridSlots.map((car, index) => (
                car
                  ? <FeaturedCarCard key={car.id} car={car} />
                  : <FeaturedCarCardPlaceholder key={`car-placeholder-${index}`} />
              ))}
            </div>
          )}
        </div>

        <div className={s.exploreAllContainer}>
          <a
            href={`${FEATURED_CAR_BASE_URL}/s`}
            className={cx(btn.landingBtn, btn.landingBtnDark, s.exploreAllBtn)}
          >
            <FormattedMessage {...messages.newLandingExploreAll} />
          </a>
        </div>
      </section>
    );
  }
}

export default injectIntl(withStyles(s, btn)(FeaturedCars));
